/**
 * Sign-in for the WEB app (app.apiboo.com, staging-app, localhost:3000).
 *
 * The desktop flow (oidcService.login) needs the Rust loopback listener and a
 * Rust HTTP client; a browser has neither. This is the standard browser version
 * of the same Authorization Code + PKCE flow, against the IdP's public client
 * "Apiboo Web App" (apiboo_idp #14):
 *
 *   beginWebLogin()      → remember state + verifier for THIS tab, redirect to
 *                          auth.apiboo.com/oauth/authorize
 *   /auth/callback       → completeWebLogin(): check state, exchange the code
 *                          with fetch (the IdP allows this origin via CORS)
 *   refreshWeb/revokeWeb → the same token endpoints, from the browser
 *
 * The verifier lives in sessionStorage only between the redirect and the
 * callback, and is deleted the moment the callback reads it — a code without
 * its verifier is worthless, so a replayed callback URL cannot sign anyone in.
 *
 * Token STORAGE is not decided here: callers hand the tokens to
 * tokenRefresher.persistTokens. Moving the refresh token into an HttpOnly
 * cookie is Web app board card 2.3.
 */
import {
  buildAuthorizeUrl,
  computeCodeChallengeS256,
  expiresAtFromExpiresIn,
  generateCodeVerifier,
  generateOAuthState,
  parseCallbackUrl,
} from '../utils/oauthPkce';

export const WEB_AUTH_BASE = (process.env.REACT_APP_AUTH_BASE_URL || 'https://auth.apiboo.com').replace(/\/+$/, '');

/** "Apiboo Web App" — public PKCE client seeded by apiboo_idp (#14). */
export const WEB_CLIENT_ID = process.env.REACT_APP_WEB_AUTH_CLIENT_ID || 'b79e3b8f-b330-4a13-bd4b-df97d45fa513';

const SCOPE = 'openid profile email offline_access apiboo.api';
const PENDING_KEY = 'apiboo.webOidc.pending';
export const WEB_CALLBACK_PATH = '/auth/callback';

export function webCallbackUri(origin = window.location.origin) {
  return `${origin}${WEB_CALLBACK_PATH}`;
}

export function isWebCallback(location = window.location) {
  return location.pathname === WEB_CALLBACK_PATH;
}

/** Start sign-in: remember state + verifier for this tab and leave for the IdP. */
export async function beginWebLogin({ provider, navigate = (url) => window.location.assign(url) } = {}) {
  const state = generateOAuthState();
  const verifier = generateCodeVerifier();
  const challenge = await computeCodeChallengeS256(verifier);
  sessionStorage.setItem(PENDING_KEY, JSON.stringify({ state, verifier, createdAt: Date.now() }));
  let url = buildAuthorizeUrl({
    authUrl: `${WEB_AUTH_BASE}/oauth/authorize`,
    clientId: WEB_CLIENT_ID,
    redirectUri: webCallbackUri(),
    scope: SCOPE,
    state,
    codeChallenge: challenge,
    // Same reason as the desktop flow: without it a signed-in IdP browser
    // session completes silently as whoever was signed in there last.
    prompt: 'login',
  });
  if (provider) url += `&provider=${encodeURIComponent(provider)}`;
  navigate(url);
}

function takePending() {
  const raw = sessionStorage.getItem(PENDING_KEY);
  // Removed BEFORE anything else happens, success or not: one callback, one use.
  sessionStorage.removeItem(PENDING_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function networkError(cause) {
  const err = new Error(cause?.message || 'Network error');
  err.isAxiosError = true;
  err.code = 'ERR_NETWORK';
  return err;
}

async function postToken(params) {
  let res;
  try {
    res = await fetch(`${WEB_AUTH_BASE}/oauth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
      body: new URLSearchParams(params).toString(),
      credentials: 'omit',
    });
  } catch (e) {
    // fetch only rejects when no response arrived (DNS, offline, CORS): the
    // network, never proof that the session is invalid.
    throw networkError(e);
  }
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    // Same shape classifyRefreshFailure produces for the desktop path, so the
    // interceptors treat an HTTP rejection (e.g. invalid_grant) as "session gone".
    const err = new Error(`Token request failed (${res.status})`);
    err.isAxiosError = true;
    err.response = { status: res.status, data };
    throw err;
  }
  return data;
}

/**
 * Finish sign-in on /auth/callback. Resolves to
 * `{ accessToken, refreshToken, expiresAt }` or throws a user-readable Error.
 */
export async function completeWebLogin(href = window.location.href) {
  const pending = takePending();
  const { code, state } = parseCallbackUrl(href);
  if (!pending || !pending.verifier) {
    throw new Error('This sign-in link has expired or was already used. Please sign in again.');
  }
  if (state !== pending.state) {
    throw new Error('Sign-in could not be verified. Please try again.');
  }
  const token = await postToken({
    grant_type: 'authorization_code',
    client_id: WEB_CLIENT_ID,
    code,
    redirect_uri: webCallbackUri(new URL(href).origin),
    code_verifier: pending.verifier,
  });
  if (!token?.access_token) throw new Error('Sign-in failed: no access token was returned.');
  return {
    accessToken: token.access_token,
    refreshToken: token.refresh_token || null,
    expiresAt: expiresAtFromExpiresIn(token.expires_in),
  };
}

/** Rotate the session from the browser (single-use refresh tokens, like desktop). */
export async function refreshWeb(refreshToken) {
  const token = await postToken({ grant_type: 'refresh_token', client_id: WEB_CLIENT_ID, refresh_token: refreshToken });
  return {
    accessToken: token.access_token,
    refreshToken: token.refresh_token || refreshToken,
    expiresAt: expiresAtFromExpiresIn(token.expires_in),
  };
}

/** Best-effort revocation on sign-out. */
export async function revokeWeb(refreshToken) {
  if (!refreshToken) return;
  try {
    await fetch(`${WEB_AUTH_BASE}/oauth/revoke`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ token: refreshToken, token_type_hint: 'refresh_token', client_id: WEB_CLIENT_ID }).toString(),
      credentials: 'omit',
    });
  } catch (e) {
    console.warn('Token revocation failed (ignored):', e?.message || e);
  }
}
